import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { accessControl } from 'lib-shared-access-control';
import { Response } from 'lib-shared-legacy-types';
import { Company, User } from 'lib-shared-types-api';
import { destroy } from 'redux-form';

import { usersApi } from 'apis/users';
import { notificationActions } from 'components/notifications/actions/notification';
import { createCompany } from 'components/shared/companyModal/api/company';
import { getRequestID } from 'components/shared/util/request';
import { pubSub } from 'utils/pubSub';
import { userTypeAssets } from 'utils/user';

import {
  addUser,
  getUser,
  presignAndUploadAvatar,
  updateUser,
  validateEmail,
  validatePhoneNumber,
} from './userModal.api';
import { UserModalCompanyCreatePayload } from './userModal.types';
import {
  AddOrUpdateUserError,
  AddUserPayload,
  AddUserResponse,
  GetUserPayload,
  GetUserResponse,
  ReadCompaniesResponse,
  UpdateUserPayload,
  UpdateUserResponse,
  ValidateEmailResponse,
  ValidatePhoneNumberResponse,
} from './userModalApi.types';

export const userModalFormName = 'userModalForm';

const prefix = 'userModal';

export const userModalActions = {
  show: createAction<{
    user: Partial<User>;
  }>(`${prefix}/show`),
  hide: createAction(`${prefix}/hide`),

  user: {
    get: createAsyncThunk<GetUserResponse, GetUserPayload>(
      `${prefix}/user/get`,
      getUser,
    ),

    add: createAsyncThunk<AddUserResponse, AddUserPayload>(
      `${prefix}/user/add`,
      async (_payload, { dispatch, rejectWithValue }) => {
        try {
          const { customRoles, ...payload } = _payload;

          const avatarUrl = payload.avatar_url
            ? await presignAndUploadAvatar(payload.avatar_url)
            : null;

          const response = await addUser({
            ...payload,
            avatar_url: avatarUrl,
          });
          const { data: createdUser } = response;

          const hasManageRoleAccess = accessControl.hasAccess(
            'organization.access',
          );

          // Assign roles to the user if the user is a member
          if (
            hasManageRoleAccess &&
            createdUser.type === 'member' &&
            customRoles
          ) {
            await dispatch(
              usersApi.endpoints.updateUserRoles.initiate({
                userId: createdUser.id,
                roleIds: customRoles.map(({ id }) => id),
              }),
            );
          }

          dispatch(userModalActions.hide());
          dispatch(destroy(userModalFormName));
          pubSub.publish('userModalUserAdded', createdUser);

          const notificationID = getRequestID();
          dispatch(
            notificationActions.notificationAdd({
              id: notificationID,
              title: 'User created',
              text: `A new ${
                userTypeAssets[createdUser.type].prettyName
              } has been created.`,
              status: 'success',
              type: 'userCreated',
              icon: 'done',
              time: 5000,
              rightAction: {
                text: 'Dismiss',
                action: () => {
                  dispatch(
                    notificationActions.notificationRemoveById({
                      id: notificationID,
                    }),
                  );
                },
              },
              leftAction: {
                text: `Go to the user`,
                action: {
                  navigate: `/contacts/user/${createdUser.id}`,
                },
              },
            }),
          );

          return response;
        } catch (error: unknown) {
          if (!error) {
            dispatch(
              notificationActions.notificationAdd({
                title: 'Error',
                text: `An error occurred while adding the new user`,
                status: 'danger',
                time: 5000,
                icon: 'exclamation',
              }),
            );
          }

          return rejectWithValue(
            (error as AxiosError<AddOrUpdateUserError>)?.response?.data,
          );
        }
      },
    ),

    update: createAsyncThunk<UpdateUserResponse, UpdateUserPayload>(
      `${prefix}/user/update`,
      async (_payload, { dispatch, rejectWithValue }) => {
        try {
          const { customRoles, ...payload } = _payload;

          const avatarUrl = payload.patch.updates?.avatar_url
            ? await presignAndUploadAvatar(payload.patch.updates.avatar_url)
            : null;

          const response = await updateUser({
            ...payload,
            ...(avatarUrl && {
              patch: {
                ...payload.patch,
                updates: {
                  ...payload.patch.updates,
                  avatar_url: avatarUrl,
                },
              },
            }),
          });
          const { data: updatedUser } = response;

          const hasManageRoleAccess = accessControl.hasAccess(
            'organization.access',
          );

          // Assign roles to the user if the user is a member
          if (
            hasManageRoleAccess &&
            updatedUser.type === 'member' &&
            customRoles
          ) {
            await dispatch(
              usersApi.endpoints.updateUserRoles.initiate({
                userId: updatedUser.id,
                roleIds: customRoles.map(({ id }) => id),
              }),
            );
          }

          pubSub.publish('userModalUserUpdated', updatedUser);
          dispatch(userModalActions.hide());
          dispatch(destroy(userModalFormName));

          dispatch(
            notificationActions.notificationAdd({
              title: 'User updated',
              text: `The user has been updated.`,
              status: 'success',
              time: 5000,
              icon: 'done',
            }),
          );

          return response;
        } catch (error: unknown) {
          if (!error) {
            dispatch(
              notificationActions.notificationAdd({
                title: 'Error',
                text: `An error occurred while updating the user`,
                status: 'danger',
                time: 5000,
                icon: 'exclamation',
              }),
            );
          }

          return rejectWithValue(
            (error as AxiosError<AddOrUpdateUserError>)?.response?.data,
          );
        }
      },
    ),
  },

  validation: {
    email: createAsyncThunk<ValidateEmailResponse['data'], string>(
      `${prefix}/validation/email`,
      async (email, { rejectWithValue }) => {
        const { data } = await validateEmail(email);
        if (data.available) {
          return data;
        }
        return rejectWithValue(data);
      },
    ),
    phoneNumber: createAsyncThunk<ValidatePhoneNumberResponse['data'], string>(
      `${prefix}/validation/phone-number`,
      async (phoneNumber, { rejectWithValue }) => {
        const { data } = await validatePhoneNumber(phoneNumber);
        if (data.available) {
          return data;
        }
        return rejectWithValue(data);
      },
    ),
  },

  companies: {
    // TODO [TEAMS-6857]: When RTK Q is ready, `fulfilled` and `rejected`
    // should be removed and `get` should be converted to `createAsyncThunk`
    // and "companies select" in "userModal" should be paginated/infinite scrolled
    get: createAction(`${prefix}/companies/get`),
    fulfilled: createAction<ReadCompaniesResponse>(
      `${prefix}/companies/fulfilled`,
    ),
    rejected: createAction<Error>(`${prefix}/companies/rejected`),

    add: createAsyncThunk(
      `${prefix}/addCompany`,
      async (payload: UserModalCompanyCreatePayload) => {
        const { data } = await (createCompany(payload) as Promise<
          Response<Company>
        >);
        return { company: data };
      },
    ),
  },
};
